import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Star, Plus, Trash2, MessageCircle, Globe, Facebook, Globe2, FileText, Filter } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { BottomNav } from '../components/BottomNav';

const sourceIcons = { google: Globe, facebook: Facebook, website: Globe2, manual: FileText };
const sourceLabels = { google: 'Google', facebook: 'Facebook', website: 'Website', manual: 'Manual' };

export const ReputationScreen = () => {
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reputation, setReputation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [filter, setFilter] = useState('all');
  const [newReview, setNewReview] = useState({ reviewer_name: '', rating: 5, review_text: '', source: 'manual' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const bizRes = await api.get('/businesses');
      const businesses = bizRes.data.businesses || [];
      if (businesses.length === 0) { navigate('/more/business-profile/new'); return; }
      const biz = businesses[0];
      setBusiness(biz);
      const res = await api.get(`/businesses/${biz.id}/reviews`);
      setReviews(res.data.reviews || []);
      setReputation(res.data.reputation);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleAddReview = async () => {
    if (!business || !newReview.reviewer_name.trim()) { toast.error('Please enter reviewer name'); return; }
    try {
      await api.post(`/businesses/${business.id}/reviews`, newReview);
      toast.success('Review added');
      setShowAdd(false);
      setNewReview({ reviewer_name: '', rating: 5, review_text: '', source: 'manual' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    try { await api.delete(`/reviews/${id}`); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  };

  const filtered = filter === 'all' ? reviews : filter === 'positive' ? reviews.filter(r => r.rating >= 4) : filter === 'negative' ? reviews.filter(r => r.rating <= 2) : reviews.filter(r => r.source === filter);
  const hasData = reputation?.status === 'sufficient_data';

  return (
    <div className="min-h-screen bg-[#F0F2F5] pb-20">
      <div className="w-full max-w-md mx-auto bg-white min-h-screen shadow-xl">
        <div className="bg-[#128C7E] text-white px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)}><ChevronLeft size={24} /></button>
          <div className="flex-1"><h1 className="text-base font-bold">Reputation</h1><p className="text-[10px] opacity-80">Understand how customers perceive your business.</p></div>
        </div>

        <div className="p-4 space-y-4">
          {loading ? (
            <div className="space-y-3 animate-pulse">
              <div className="h-28 bg-gray-100 rounded-2xl" />
              <div className="h-12 bg-gray-100 rounded-xl" />
              <div className="h-24 bg-gray-100 rounded-xl" />
            </div>
          ) : (<>
          {/* Reputation Summary */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-5 border border-amber-100" data-testid="reputation-summary">
            {hasData ? (
              <div className="text-center">
                <p className="text-4xl font-bold text-slate-800">{reputation.average_rating}</p>
                <div className="flex items-center justify-center gap-0.5 my-2">
                  {[1, 2, 3, 4, 5].map(s => <Star key={s} size={20} className={s <= Math.round(reputation.average_rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'} />)}
                </div>
                <p className="text-sm text-gray-600">Based on {reputation.review_count} reviews</p>
              </div>
            ) : (
              <div className="text-center py-4">
                <Star size={32} className="text-gray-300 mx-auto mb-3" />
                <h3 className="text-base font-bold text-slate-700 mb-1">Reputation Insights</h3>
                <p className="text-xs text-gray-500">We are still gathering enough information about your business. Add reviews or connect a review source to build your reputation profile.</p>
              </div>
            )}
          </div>

          {/* Add Review Button */}
          <button onClick={() => setShowAdd(true)}
            className="w-full h-12 bg-[#128C7E] text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
            data-testid="add-review-btn"
          >
            <Plus size={16} /> Add Manual Review
          </button>

          {/* Add Review Form */}
          {showAdd && (
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 space-y-3" data-testid="review-form">
              <h4 className="text-sm font-bold text-slate-700">Add Customer Review</h4>
              <p className="text-[10px] text-amber-600 bg-amber-50 px-2 py-1 rounded">Manual reviews are clearly distinguished from verified external reviews.</p>
              <input value={newReview.reviewer_name} onChange={e => setNewReview(p => ({...p, reviewer_name: e.target.value}))}
                placeholder="Customer name" className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#128C7E]" data-testid="review-name" />
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Rating</label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map(s => (
                    <button key={s} onClick={() => setNewReview(p => ({...p, rating: s}))} data-testid={`review-star-${s}`}>
                      <Star size={28} className={s <= newReview.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200'} />
                    </button>
                  ))}
                </div>
              </div>
              <textarea value={newReview.review_text} onChange={e => setNewReview(p => ({...p, review_text: e.target.value}))}
                placeholder="Review text..." rows={3} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#128C7E] resize-none" data-testid="review-text" />
              <div className="flex gap-2">
                <button onClick={handleAddReview} className="flex-1 py-2.5 bg-[#128C7E] text-white rounded-lg text-sm font-bold" data-testid="submit-review">Save Review</button>
                <button onClick={() => setShowAdd(false)} className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium">Cancel</button>
              </div>
            </div>
          )}

          {/* Filters */}
          {reviews.length > 0 && (
            <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="review-filters">
              {[{ id: 'all', label: 'All' }, { id: 'positive', label: 'Positive' }, { id: 'negative', label: 'Negative' }, { id: 'manual', label: 'Manual' }].map(f => (
                <button key={f.id} onClick={() => setFilter(f.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${filter === f.id ? 'bg-[#128C7E] text-white' : 'bg-gray-100 text-gray-600'}`}>
                  {f.label}
                </button>
              ))}
            </div>
          )}

          {/* Review List */}
          {filtered.length > 0 ? (
            <div className="space-y-2" data-testid="review-list">
              {filtered.map(r => {
                const SourceIcon = sourceIcons[r.source] || FileText;
                return (
                  <div key={r.id} className="bg-white border border-gray-200 rounded-xl p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-slate-800">{r.reviewer_name || 'Anonymous'}</span>
                          <span className="flex items-center gap-0.5 text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                            <SourceIcon size={10} />{sourceLabels[r.source] || r.source}
                          </span>
                          {r.source === 'manual' && <span className="text-[8px] bg-amber-50 text-amber-600 px-1 rounded">Unverified</span>}
                        </div>
                        <div className="flex gap-0.5 mb-1.5">
                          {[1, 2, 3, 4, 5].map(s => <Star key={s} size={12} className={s <= r.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200'} />)}
                        </div>
                        {r.review_text && <p className="text-xs text-gray-700 leading-relaxed">{r.review_text}</p>}
                      </div>
                      <button onClick={() => handleDelete(r.id)} className="p-1 text-gray-300 hover:text-red-500"><Trash2 size={14} /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle size={32} className="text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No reviews yet. Add customer testimonials or connect a review source.</p>
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">No reviews match this filter.</p>
          )}

          {/* Connect Sources */}
          <div className="space-y-2" data-testid="connect-sources">
            <h3 className="text-sm font-bold text-slate-700">Connect Review Sources</h3>
            <p className="text-xs text-gray-500 mb-2">Connect supported platforms to automatically centralize customer feedback.</p>
            {['Google Reviews', 'Facebook Reviews'].map(src => (
              <div key={src} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center gap-2">
                  <Globe size={16} className="text-gray-400" />
                  <div>
                    <p className="text-xs font-semibold text-slate-700">{src}</p>
                    <p className="text-[10px] text-gray-400">Not Connected</p>
                  </div>
                </div>
                <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">Coming Soon</span>
              </div>
            ))}
          </div>

          {/* Review Insights */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100" data-testid="review-insights">
            <h3 className="text-sm font-bold text-purple-800 mb-1">Review Insights</h3>
            {reviews.length >= 3 ? (
              <p className="text-xs text-purple-700">Based on {reviews.length} reviews, your average rating is {reputation?.average_rating}. {reputation?.average_rating >= 4 ? 'Customers are generally satisfied.' : 'There is room to improve customer satisfaction.'}</p>
            ) : (
              <p className="text-xs text-purple-600">Review insights will appear as more customer feedback becomes available.</p>
            )}
          </div>
          </>)}
        </div>
      </div>
      <BottomNav />
    </div>
  );
};
